var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// client/x-image.ts
import "@adornis/base/utilTypes.js";
import { ComponentSizeController } from "@adornis/chemistry/controllers/component-size-controller.js";
import "@adornis/chemistry/controllers/loader-controller.js";
import { RXController } from "@adornis/chemistry/controllers/RXController.js";
import { event } from "@adornis/chemistry/decorators/event.js";
import { css } from "@adornis/chemistry/directives/css.js";
import "@adornis/chemistry/elements/components/x-flex.js";
import "@adornis/chemistry/elements/components/x-loader.js";
import { loaderSymbol } from "@adornis/chemistry/symbols.js";
import { FormField } from "@adornis/formfield/form-field.js";
import { html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { firstValueFrom, fromEvent, switchMap, timer } from "rxjs";
import { filter } from "rxjs/operators";
import { ImageFile } from "../db/images.js";
import { askForImage } from "./helper-methods.js";
var XImage = class extends FormField {
  constructor() {
    super(...arguments);
    this.mode = "cover";
    this.random = false;
    this["preview-src"] = "";
    this.fileEntity = ImageFile;
    this.fileSize = 0;
    this.fileName = "";
    this.aspectratio = void 0;
    this.size = new ComponentSizeController(this, { debounceTime: 200 });
    this.imageToUpload = null;
    this._imageToUploadPreview = "";
    this.loaded = false;
    this.errorOnSource = false;
    this.placeholder = "";
    this.alt = "";
    this.loading = "lazy";
    this._lastSrc = "";
    this.lowResLoaded = false;
    this.lowResInformation = new RXController(
      this,
      this.value.observable.pipe(
        filter((value) => isObjectID(value)),
        switchMap((val) => this.fileEntity.subscribeByID(val)({ meta: { previewBase64: 1 } }))
      )
    );
  }
  styles() {
    return [{ ":host": { display: "block", position: "relative" } }];
  }
  updated(_changedProperties) {
    super.updated(_changedProperties);
    if (!this.loaded && this.loading === "eager") {
      const loader = this.getRootNode().host[loaderSymbol];
      if (!loader)
        return console.warn("was asked to wait on image eagerly, but no loader was found", this);
      loader.waitFor(fromEvent(this, "load"));
    }
  }
  async commit() {
    const id = await this.uploadToDatabase();
    if (typeof id === "string") {
      this.value.next(id);
      this.dispatchEvent(new CustomEvent("value-picked", { detail: { value: id } }));
      this._imageToUploadPreview = "";
    }
    return id;
  }
  clear() {
    this.value.next(void 0);
    this.dispatchEvent(new CustomEvent("value-picked", { detail: { value: null } }));
    this.imageToUpload = null;
    this._imageToUploadPreview = "";
    this.fileSize = 0;
    this.fileName = "";
  }
  async preValidate() {
    await this.commit();
  }
  postValidate(errored) {
  }
  get internalSource() {
    const newSrc = this.calculateInternalSource();
    if (newSrc !== this._lastSrc) {
      this._lastSrc = newSrc;
      this.loaded = false;
    }
    return newSrc;
  }
  calculateInternalSource() {
    if (this._imageToUploadPreview)
      return this._imageToUploadPreview;
    if (!this.size.height || !this.size.width) {
      console.warn(
        "your image will possibly never finish loading because you have not given it a size and it is mode cover",
        this,
        this.value.value,
        this.size.width,
        this.size.height
      );
    }
    const width = this.size.width ?? 0 * window.devicePixelRatio;
    const height = this.size.height ?? 0 * window.devicePixelRatio;
    const requestedWidth = this.mode === "contain" ? Math.max(Math.ceil(width), Math.ceil(height)) : Math.ceil(width);
    const requestedHeight = ["width", "contain"].includes(this.mode) ? "" : Math.ceil(height);
    if (this.random)
      return `https://picsum.photos/${requestedWidth}/${requestedHeight}`;
    const defaultDummy = `https://dummyimage.com/${requestedWidth}x${requestedHeight}/9e579e/2732c4.png`;
    const errorImage = `https://dummyimage.com/${requestedWidth}x${requestedHeight}&text=404`;
    if (this.errorOnSource)
      return errorImage;
    if (this.value.value) {
      if (isObjectID(this.value.value))
        return this.fileEntity.getServeLink(this.value.value, { size: `${requestedWidth}x${requestedHeight}` });
      else
        return this.value.value;
    }
    if (this.placeholder)
      return this.placeholder;
    return this["preview-src"] || defaultDummy + "&text=Dummy+Image";
  }
  async uploadToDatabase() {
    const file = this.imageToUpload;
    if (!file)
      return;
    if (!(file instanceof File))
      return alert("choose File");
    const imageID = await this.fileEntity.upload(file);
    await firstValueFrom(timer(1e3));
    return imageID;
  }
  handleSelectedFile(file) {
    if (!file)
      return;
    this.fileSize = file.size;
    this.fileName = file.name;
    this.imageToUpload = file;
    const reader = new FileReader();
    reader.onload = (e) => this._imageToUploadPreview = reader.result;
    reader.readAsDataURL(file);
    this.preview = true;
    this.dispatchEvent(new CustomEvent("image-chosen", { detail: { value: file } }));
  }
  _errorCallback() {
    if (this.loaded)
      return;
    this.loaded = true;
    this.errorOnSource = true;
    this.dispatchEvent(new Event("error"));
    this.requestUpdate();
  }
  render() {
    const wantsAspectRatio = !!this.aspectratio && !isNaN(+this.aspectratio);
    const dimensionStyle = {
      width: !!wantsAspectRatio ? this.offsetHeight * +this.aspectratio + "px" : "100%",
      height: !!wantsAspectRatio ? this.offsetWidth / +this.aspectratio + "px" : "100%"
    };
    return html`
      <img
        @load=${() => this._loadedCallback()}
        @error=${() => this.internalSource ? this._errorCallback() : () => {
    }}
        src=${this.internalSource}
        alt=${this.alt}
        ${css({
      ...dimensionStyle,
      objectFit: this.mode === "width" ? "contain" : this.mode,
      objectPosition: "center",
      display: "block"
    })}
      />
      ${this.editOverlay()} ${this.loader()}
    `;
  }
  editOverlay() {
    return !this.disabled ? html`
          <x-flex
            ${css({ position: "absolute", inset: "0", cursor: "pointer" })}
            @click=${() => askForImage().then((file) => this.handleSelectedFile(file)).catch((e) => console.warn("There was a problem selecting a photo: ", e))}
          >
            ${this.value.value || this.imageToUpload ? nothing : html` <slot ${css({ position: "relative", zIndex: "1" })} name="placeholder"> </slot> `}
          </x-flex>
        ` : nothing;
  }
  loader() {
    const lowRes = html`
      <div
        ${css({
      position: "absolute",
      inset: "0",
      width: "100%",
      height: "100%",
      background: this.colors.neutral.shade(200),
      opacity: this.loaded ? "0" : "1",
      pointerEvents: this.loaded ? "none" : "initial"
    })}
      ></div>
      <img
        ${css({
      position: "absolute",
      inset: "0",
      width: "100%",
      height: "100%",
      opacity: this.loaded || !this.lowResLoaded ? "0" : "1",
      transition: "all .3s ease",
      pointerEvents: this.loaded ? "none" : "initial"
    })}
        alt=${this.alt}
        src=${`data:image/png;base64,${this.lowResInformation.value?.meta?.previewBase64}`}
        @load=${() => this.lowResLoaded = true}
      />
    `;
    const loader = html`
      <x-flex
        h100
        w100
        center
        crossaxis-center
        ${css({
      position: "absolute",
      inset: "0",
      backdropFilter: this.loaded ? "unset" : "blur(80px)",
      opacity: this.loaded ? "0" : "1",
      transition: "all .2s ease",
      pointerEvents: this.loaded ? "none" : "initial"
    })}
      >
        <x-loader></x-loader>
      </x-flex>
    `;
    if (this.lowResInformation.value)
      return lowRes;
    else
      return loader;
  }
  _loadedCallback() {
    this.loaded = true;
    this.dispatchEvent(new Event("load"));
  }
};
__decorateClass([
  property()
], XImage.prototype, "mode", 2);
__decorateClass([
  property({ type: Boolean })
], XImage.prototype, "random", 2);
__decorateClass([
  property()
], XImage.prototype, "preview-src", 2);
__decorateClass([
  event,
  state()
], XImage.prototype, "fileSize", 2);
__decorateClass([
  event,
  state()
], XImage.prototype, "fileName", 2);
__decorateClass([
  property({ type: Number })
], XImage.prototype, "aspectratio", 2);
__decorateClass([
  state()
], XImage.prototype, "size", 2);
__decorateClass([
  event,
  state()
], XImage.prototype, "imageToUpload", 2);
__decorateClass([
  state()
], XImage.prototype, "_imageToUploadPreview", 2);
__decorateClass([
  property({ reflect: true, type: Boolean })
], XImage.prototype, "loaded", 2);
__decorateClass([
  property({ reflect: true, type: Boolean })
], XImage.prototype, "errorOnSource", 2);
__decorateClass([
  property()
], XImage.prototype, "placeholder", 2);
__decorateClass([
  property({ type: String })
], XImage.prototype, "alt", 2);
__decorateClass([
  property({ type: String, reflect: true })
], XImage.prototype, "loading", 2);
__decorateClass([
  state()
], XImage.prototype, "lowResLoaded", 2);
__decorateClass([
  state()
], XImage.prototype, "lowResInformation", 2);
XImage = __decorateClass([
  customElement("x-image")
], XImage);
var isObjectID = (value) => {
  return !!value && !!value.match(/^[a-f0-9]{24}$/);
};
export {
  XImage
};
